import * as dayjs from "dayjs";
import * as isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import {
    REQUESTED_AMOUNT_FEE,
    SIMPLES_NACIONAL_DAYLY_IOF_RATE,
    DEFAULT_DAYLY_IOF_FEE,
    PAST_ONE_YEAR_IOF_FEE,
    REQUESTED_AMOUNT_LIMIT_TO_SIMPLES_NACIONAL,
    PAST_ONE_YEAR_SIMPLES_NACIONAL_IOF_FEE
} from "./iof.constants";
import { calculateInstallmentDates } from "../commons";
import { PaymentInterval } from "../simulator/simulator";

export interface IIOFCalculateParams {
    amount: number;
    tenure: number;
    loanDate: Date,
    dueDate: Date;
    isSimplesNacional: boolean;
    paymentInterval: PaymentInterval;
    downPayments?: number;
}

export const calculateIof = (params: IIOFCalculateParams): number => {
    const {
        amount, tenure, loanDate, dueDate, isSimplesNacional, paymentInterval, downPayments
    } = params;

    if(matchesCoronaVirusInterval(dayjs(loanDate))) {
        return 0;
    }

    const totalInstallments = tenure + (downPayments ?? 0);
    const amortization = amount / totalInstallments;
    const isElegibleForSimplesNacional =
        isSimplesNacional && amount < REQUESTED_AMOUNT_LIMIT_TO_SIMPLES_NACIONAL;
    const dailyIofFee = isElegibleForSimplesNacional
        ? SIMPLES_NACIONAL_DAYLY_IOF_RATE : DEFAULT_DAYLY_IOF_FEE;
    const pastOneYearIofFee = isElegibleForSimplesNacional
        ? PAST_ONE_YEAR_SIMPLES_NACIONAL_IOF_FEE : PAST_ONE_YEAR_IOF_FEE;

    const IofAppliedToAmortization = calculateInstallmentDates(totalInstallments, dueDate, paymentInterval)
        .map((date: Date) => {
            const daysAmount = dayjs(date).diff(dayjs(loanDate), "day");

            if (daysAmount > 365) {
                return amortization * pastOneYearIofFee;
            }

            return amortization * daysAmount * dailyIofFee;
        })
        .reduce((acc: number, next: number) => acc + next, 0);

    const additionalIof = amount * REQUESTED_AMOUNT_FEE;

    return calculateIofConsideringWithholding(amount, additionalIof + IofAppliedToAmortization);
}

/**
 * Calculate IOF value considering the IOF withholding in requested amount.
 * The amount that the customer will receive is the requestedAmount - IOF then we need to calculate the IOF considering the withholding.
 */
const calculateIofConsideringWithholding = (loanGrossValue: number, iof: number) =>
    loanGrossValue * iof / (loanGrossValue - iof);

const matchesCoronaVirusInterval = (loanDate: dayjs.Dayjs) =>
    loanDate.isSameOrAfter(dayjs('2020-12-15')) && loanDate.isSameOrBefore(dayjs('2020-12-31'));